/**
 * Footer nav dropdown (mobile only, ≤768px).
 * Toggles submenu visibility when the column toggle button is clicked.
 */
export class FooterNavDropdown {
    constructor(container) {
        this.container = container;
        this.buttons = [];
        this.boundHandlers = new Map();
    }

    init() {
        if (!this.container) return;
        this.buttons = Array.from(
            this.container.querySelectorAll("[data-footer-nav-toggle]")
        );
        this.buttons.forEach((btn) => {
            const handler = (e) => this.handleClick(e, btn);
            this.boundHandlers.set(btn, handler);
            btn.addEventListener("click", handler);
        });
    }

    handleClick(e, button) {
        const isMobile = window.matchMedia("(max-width: 768px)").matches;
        if (isMobile) {
            e.preventDefault();
            this.toggle(button);
        } else {
            const href = button.getAttribute("data-footer-nav-href");
            if (href) window.location.href = href;
        }
    }

    toggle(button) {
        const column = button.closest("[data-footer-nav-column]");
        if (!column) return;
        const isOpen = column.classList.toggle("no-footer__column--open");
        button.setAttribute("aria-expanded", String(isOpen));
    }

    destroy() {
        this.buttons.forEach((btn) => {
            const handler = this.boundHandlers.get(btn);
            if (handler) btn.removeEventListener("click", handler);
        });
        this.boundHandlers.clear();
        this.buttons = [];
    }
}

/**
 * Footer Component
 * Handles mouse tracking and gradient circle animation with smooth lerp interpolation
 */
export class Footer {
    constructor() {
        this.footer = null;
        this.gradient = null;
        this.footerNavDropdown = null;
        this.mouseX = 0;
        this.mouseY = 0;
        this.targetX = 0;
        this.targetY = 0;
        this.currentX = 0;
        this.currentY = 0;
        this.animationFrame = null;
        this.lastTime = 0;
        this.lerpFactor = 0.15; // Lerp interpolation factor (0-1, higher = faster)
    }

    /**
     * Linear interpolation function
     * @param {number} start - Starting value
     * @param {number} end - Target value
     * @param {number} t - Interpolation factor (0-1)
     * @returns {number} Interpolated value
     */
    lerp(start, end, t) {
        return start + (end - start) * t;
    }

    /**
     * Smooth lerp with delta time consideration for consistent animation speed
     * @param {number} start - Starting value
     * @param {number} end - Target value
     * @param {number} deltaTime - Time difference in milliseconds
     * @param {number} speed - Animation speed (default: 0.015)
     * @returns {number} Interpolated value
     */
    smoothLerp(start, end, deltaTime, speed = 0.015) {
        const t = 1 - Math.pow(1 - speed, deltaTime / 16.67); // Normalize to 60fps
        return this.lerp(start, end, t);
    }

    init() {
        this.footer = document.getElementById("main-footer");
        if (!this.footer) return;

        this.footerNavDropdown = new FooterNavDropdown(this.footer);
        this.footerNavDropdown.init();

        this.gradient = this.footer.querySelector(".no-footer__gradient");
        if (!this.gradient) return;

        // Initialize position
        const rect = this.footer.getBoundingClientRect();
        this.currentX = rect.width / 2;
        this.currentY = rect.height / 2;
        this.targetX = this.currentX;
        this.targetY = this.currentY;

        // Bind event handlers
        this.handleMouseMoveBound = (e) => this.handleMouseMove(e);
        this.handleMouseLeaveBound = () => this.handleMouseLeave();

        // Add mouse move listener
        this.footer.addEventListener("mousemove", this.handleMouseMoveBound);

        // Add mouse leave listener to reset position
        this.footer.addEventListener("mouseleave", this.handleMouseLeaveBound);

        // Start animation loop
        this.animate();
    }

    handleMouseMove(e) {
        const rect = this.footer.getBoundingClientRect();
        this.targetX = e.clientX - rect.left;
        this.targetY = e.clientY - rect.top;
    }

    handleMouseLeave() {
        const rect = this.footer.getBoundingClientRect();
        // Reset to center
        this.targetX = rect.width / 2;
        this.targetY = rect.height / 2;
    }

    animate(currentTime = 0) {
        // Calculate delta time for frame-rate independent animation
        const deltaTime = currentTime - this.lastTime || 16.67; // Default to 60fps if first frame
        this.lastTime = currentTime;

        // Use smooth lerp for frame-rate independent smooth animation
        this.currentX = this.smoothLerp(
            this.currentX,
            this.targetX,
            deltaTime,
            this.lerpFactor
        );
        this.currentY = this.smoothLerp(
            this.currentY,
            this.targetY,
            deltaTime,
            this.lerpFactor
        );

        // Update CSS custom properties with sub-pixel precision
        this.footer.style.setProperty("--mouse-x", `${this.currentX}px`);
        this.footer.style.setProperty("--mouse-y", `${this.currentY}px`);

        // Continue animation loop with timestamp
        this.animationFrame = requestAnimationFrame((timestamp) => {
            this.animate(timestamp);
        });
    }

    destroy() {
        if (this.footerNavDropdown) {
            this.footerNavDropdown.destroy();
            this.footerNavDropdown = null;
        }
        if (this.animationFrame) {
            cancelAnimationFrame(this.animationFrame);
            this.animationFrame = null;
        }
        if (this.footer) {
            if (this.handleMouseMoveBound) {
                this.footer.removeEventListener("mousemove", this.handleMouseMoveBound);
            }
            if (this.handleMouseLeaveBound) {
                this.footer.removeEventListener("mouseleave", this.handleMouseLeaveBound);
            }
        }
        this.lastTime = 0;
    }
}

